import React, { useCallback, useMemo, useState } from 'react';
import { Container, Grid, makeStyles, Paper } from '@material-ui/core';

import BreadCrumb from 'app/components/BreadCrumb';
import LinesRegistration from 'app/components/ServicesForms/Lines/LinesRegistration';

import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FormServiceContext } from 'app/utils/contexts';
import { SERVICES_PROPERTIES } from 'app/config/services';
import { URLS } from 'app/config/urls';
import { STATUS_CODES } from 'app/config/status';

import lineEndpoint from 'app/api/services/lineEndpoint';
import alertsEndpoint from 'app/api/services/alertsEndpoint';

import notification from 'app/config/notification';
import withRequestInitiation from 'app/components/Hoc/withRequestInitiation';

import { preparePayload } from 'app/utils/forms/functions';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import { fetchSaveRequest, updateRequestDetails } from 'app/store/slices/requests';
import { errorHandler } from 'app/utils/errorHandler';

import styles from './styles';
import { validateLineRequest } from 'app/utils/forms/validators/line';
import { setupLinePayload } from 'app/utils/forms/services/lines';

const useStyles = makeStyles(styles);

function Lines(props) {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    intl,
    loadingDocuments,
    setLoadingDocuments,
    canEdit,
    editing,
    resetAndGo,
    onCancel,
    validateForm,
    breadCrumb,
    rightInfo,
  } = props;

  const [errorsList, setErrorsList] = useState({});

  const documentsStore = useSelector(state => state.services.documents);
  const service = useSelector(state => state.services.details);
  const lines = useSelector(state => state.line.allLines);
  const selectedLineId = useSelector(state => state.line.selectedLineId);
  const linesRequest = useSelector(state => state.line.selectedLinesRequest);
  const schedulesStore = useSelector(state => state.line.schedulesStore);
  const selectedEquipmentType = useSelector(state => state.line.selectedEquipmentType);
  const selectedEquipmentClassification = useSelector(
    state => state.line.selectedEquipmentClassification
  );
  const selectedSubstituteEquipmentType = useSelector(
    state => state.line.selectedSubstituteEquipmentType
  );
  const selectedSubstituteEquipmentClassification = useSelector(
    state => state.line.selectedSubstituteEquipmentClassification
  );
  const request = useSelector(state => state.requests.details);
  const activeCompany = useSelector(state => state.company.activeCompany);
  const registrationUserInfo = useSelector(state => state.auth.registrationInfo);
  const userAdmin = useSelector(state => state.auth.userAdmin);
  const inspectedVehicles = useSelector(state => state.vehicle.inspectedVehicles);

  const initialValues = useMemo(() => {
    if (editing) {
      return JSON.parse(request.jsonSolicitacao);
    }
    if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues) {
      const temp = { ...SERVICES_PROPERTIES[service.cdServico].formInitialValues };
      temp.documentos = [];
      return temp;
    }
    return {};
  }, [editing, request.jsonSolicitacao, service.cdServico]);

  const onSave = useCallback(
    async (values, fromSubmit, isInternalForm) => {
      if (!canEdit) {
        if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
      }

      const linhaSelecionada = lines.find(line => line.cdLinha === selectedLineId);

      const valuesCopy = setupLinePayload({
        values,
        cdServico: service.cdServico,
        linha: linhaSelecionada,
        linhasSolicitacao: linesRequest,
        horarios: schedulesStore,
        inspectedVehicles: inspectedVehicles,
        equipamento: {
          tipo: selectedEquipmentType,
          categoria: selectedEquipmentClassification,
        },
        equipamentoSubst: {
          tipo: selectedSubstituteEquipmentType,
          categoria: selectedSubstituteEquipmentClassification,
        },
      });

      if (fromSubmit) {
        const errors = validateLineRequest({
          values: valuesCopy,
          cdServico: service.cdServico,
          intl,
          isInternalForm,
        });
        if (Object.keys(errors).length > 0) {
          setErrorsList(errors);
          return;
        } else {
          setErrorsList({});
        }
      }

      const documentsObject = {
        store: documentsStore,
        files: valuesCopy.documentos,
      };

      if (request?.situacao) valuesCopy.situacao = request.situacao;

      let dataToPayload = {
        cdServico: service.cdServico,
      };

      if (request && request.cdServico) {
        dataToPayload = { ...request };
      }

      const preparedPayload = preparePayload({
        values: valuesCopy,
        documents: documentsObject,
        params: dataToPayload,
        activeCompany,
        userAdmin,
        registrationUserInfo,
      });

      dispatch(showLoading());
      let showMessage = false;
      let response = false;
      if (!fromSubmit) showMessage = true;
      try {
        response = await fetchSaveRequest({
          isFormData: true,
          preparedPayload,
          dispatch,
          intl,
          showMessage,
          cdService: service.cdServico,
        });
        if (response?.status === 200 && response?.data) {
          await dispatch(updateRequestDetails(response.data));
        }
      } catch (error) {
        errorHandler(error.response?.status, error.response?.data?.message);
      }
      if (!fromSubmit) dispatch(hideLoading());
      return response;
    },
    // eslint-disable-next-line
    [
      canEdit,
      lines,
      linesRequest,
      schedulesStore,
      inspectedVehicles,
      selectedLineId,
      selectedEquipmentType,
      selectedEquipmentClassification,
      selectedSubstituteEquipmentType,
      selectedSubstituteEquipmentClassification,
      documentsStore,
      request,
      activeCompany,
      userAdmin,
      registrationUserInfo,
      dispatch,
      intl,
    ]
  );

  const onSubmit = useCallback(
    async values => {
      if (!canEdit) {
        if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
      }

      const responseSave = await onSave(values, true);
      if (responseSave && responseSave.status === 200) {
        try {
          const response = await lineEndpoint.finishRequest(responseSave.data.cdSolicitacao);
          if (response.status === 200) {
            notification(
              intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }),
              { toastId: 'SUCCESSFUL_FINISHED_REQUEST', type: 'success' }
            );

            if (request.cdSituacao === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
              await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
            }
          }
          resetAndGo();
        } catch (error) {
          errorHandler(error.response?.status, error.response?.data?.message);
        }
      }
      dispatch(hideLoading());
    },
    [
      canEdit,
      dispatch,
      intl,
      onSave,
      request.cdSituacao,
      request.cdSolicitacao,
      request.situacao,
      resetAndGo,
    ]
  );

  const contextProvider = useMemo(() => {
    return {
      loadingDocuments,
      setLoadingDocuments,
      validateForm,
      onSubmit,
      onSave,
      onCancel,
      initialValues,
      validationSchema: SERVICES_PROPERTIES[service.cdServico]
        ? SERVICES_PROPERTIES[service.cdServico].validationSchema
        : {},
      canEdit,
      editing,
      errorsList,
      setErrorsList,
    };
  }, [
    loadingDocuments,
    setLoadingDocuments,
    validateForm,
    onSubmit,
    onSave,
    onCancel,
    initialValues,
    service.cdServico,
    canEdit,
    editing,
    errorsList,
    setErrorsList,
  ]);

  const handleOnBack = () => {
    if (request.nuSolicitacaoFmt && request.nuSolicitacaoFmt !== '') {
      history.push(URLS.START);
    } else {
      history.push(URLS.SERVICES);
    }
  };

  return (
    <Container>
      <BreadCrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.container} elevation={3}>
            <FormServiceContext.Provider value={contextProvider}>
              <LinesRegistration {...props} />
            </FormServiceContext.Provider>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default injectIntl(withRequestInitiation(Lines));
